import { useState, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTheme } from '@/hooks/useTheme';
import { SystemStateBanner } from '@/components/SystemStateBanner';

interface NavItem {
  path: string;
  label: string;
  iconPath: string;
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    iconPath: 'M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15',
  },
  {
    path: '/watchlist',
    label: 'Watchlist',
    iconPath: 'M18 20V10M12 20V4M6 20V14',
  },
  {
    path: '/activity',
    label: 'Activity',
    iconPath: 'M12 8V12L14.5 14.5M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z',
  },
  {
    path: '/settings',
    label: 'Settings',
    iconPath: 'M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15ZM19.4 15C19.1277 15.6171 19.2583 16.3378 19.73 16.82L19.79 16.88C20.1656 17.2551 20.3766 17.7642 20.3766 18.295C20.3766 18.8258 20.1656 19.3349 19.79 19.71C19.4149 20.0856 18.9058 20.2966 18.375 20.2966C17.8442 20.2966 17.3351 20.0856 16.96 19.71L16.9 19.65C16.4178 19.1783 15.6971 19.0477 15.08 19.32C14.4755 19.5791 14.0826 20.1724 14.08 20.83V21C14.08 22.1046 13.1846 23 12.08 23C10.9754 23 10.08 22.1046 10.08 21V20.91C10.0642 20.2327 9.63587 19.6339 9 19.4C8.38291 19.1277 7.66219 19.2583 7.18 19.73L7.12 19.79C6.74493 20.1656 6.23584 20.3766 5.705 20.3766C5.17416 20.3766 4.66507 20.1656 4.29 19.79C3.91445 19.4149 3.70343 18.9058 3.70343 18.375C3.70343 17.8442 3.91445 17.3351 4.29 16.96L4.35 16.9C4.82167 16.4178 4.95235 15.6971 4.68 15.08C4.42093 14.4755 3.82764 14.0826 3.17 14.08H3C1.89543 14.08 1 13.1846 1 12.08C1 10.9754 1.89543 10.08 3 10.08H3.09C3.76733 10.0642 4.36613 9.63587 4.6 9C4.87235 8.38291 4.74167 7.66219 4.27 7.18L4.21 7.12C3.83445 6.74493 3.62343 6.23584 3.62343 5.705C3.62343 5.17416 3.83445 4.66507 4.21 4.29C4.58507 3.91445 5.09416 3.70343 5.625 3.70343C6.15584 3.70343 6.66493 3.91445 7.04 4.29L7.1 4.35C7.58219 4.82167 8.30291 4.95235 8.92 4.68H9C9.60447 4.42093 9.99738 3.82764 10 3.17V3C10 1.89543 10.8954 1 12 1C13.1046 1 14 1.89543 14 3V3.09C14.0026 3.74764 14.3955 4.34093 15 4.6C15.6171 4.87235 16.3378 4.74167 16.82 4.27L16.88 4.21C17.2551 3.83445 17.7642 3.62343 18.295 3.62343C18.8258 3.62343 19.3349 3.83445 19.71 4.21C20.0856 4.58507 20.2966 5.09416 20.2966 5.625C20.2966 6.15584 20.0856 6.66493 19.71 7.04L19.65 7.1C19.1783 7.58219 19.0477 8.30291 19.32 8.92V9C19.5791 9.60447 20.1724 9.99738 20.83 10H21C22.1046 10 23 10.8954 23 12C23 13.1046 22.1046 14 21 14H20.91C20.2524 14.0026 19.6591 14.3955 19.4 15Z',
  },
];

function NavIcon({ d, active }: { d: string; active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

function ThemeCycleButton() {
  const { theme, setTheme } = useTheme();

  const next: Record<string, 'light' | 'dark' | 'system'> = {
    light: 'dark',
    dark: 'system',
    system: 'light',
  };
  const icons: Record<string, string> = {
    light: 'M12 3V4M12 20V21M4 12H3M21 12H20M18.364 5.636L17.657 6.343M6.343 17.657L5.636 18.364M18.364 18.364L17.657 17.657M6.343 6.343L5.636 5.636M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z',
    dark: 'M21.9548 12.9564C20.5779 15.3717 17.9791 17.0001 15 17.0001C10.5817 17.0001 7 13.4183 7 9.00005C7 6.02097 8.62837 3.42219 11.0437 2.04529C5.96046 2.52441 2 6.79961 2 12C2 17.5228 6.47715 22 12 22C17.2004 22 21.4756 18.0396 21.9548 12.9564Z',
    system: 'M2 8C2 6.89543 2.89543 6 4 6H20C21.1046 6 22 6.89543 22 8V16C22 17.1046 21.1046 18 20 18H4C2.89543 18 2 17.1046 2 16V8Z',
  };

  return (
    <Box
      as="button"
      onClick={() => setTheme(next[theme])}
      title={`Theme: ${theme}`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="40px"
      h="40px"
      borderRadius="md"
      bg="transparent"
      border="none"
      color="text.tertiary"
      cursor="pointer"
      transition="background 150ms ease"
      _hover={{ bg: 'bg.tertiary' }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={icons[theme]} />
      </svg>
    </Box>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isExpanded = expanded || pinned;

  function handleMouseEnter() {
    hoverTimer.current = setTimeout(() => setExpanded(true), 150);
  }

  function handleMouseLeave() {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (!pinned) setExpanded(false);
  }

  function handleTogglePin() {
    setPinned((p) => !p);
    setExpanded(true);
  }

  return (
    <Box display="flex" minH="100vh">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top="0"
        left="0"
        h="100vh"
        w={isExpanded ? '240px' : '64px'}
        flexDirection="column"
        bg="bg.secondary"
        borderRight="1px solid"
        borderColor="border.secondary"
        zIndex="10"
        transition="width 200ms ease"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        overflow="hidden"
      >
        {/* Logo */}
        <Flex
          align="center"
          h="64px"
          px={isExpanded ? '5' : '0'}
          justify={isExpanded ? 'flex-start' : 'center'}
          flexShrink={0}
        >
          {isExpanded ? (
            <Text fontSize="18px" fontWeight="600" color="text.primary" whiteSpace="nowrap">
              Market Buddy
            </Text>
          ) : (
            <Text fontSize="18px" fontWeight="700" color="text.brand">
              MB
            </Text>
          )}
        </Flex>

        {/* Nav items */}
        <Flex direction="column" flex="1" gap="2px" mt="2">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Box
                as="button"
                key={item.path}
                onClick={() => navigate(item.path)}
                title={!isExpanded ? item.label : undefined}
                display="flex"
                alignItems="center"
                gap="3"
                px={isExpanded ? '5' : '0'}
                justifyContent={isExpanded ? 'flex-start' : 'center'}
                h="44px"
                w="full"
                color={isActive ? 'text.brand' : 'text.secondary'}
                bg={isActive ? 'bg.active' : 'transparent'}
                borderLeft={isActive ? '3px solid' : '3px solid transparent'}
                borderColor={isActive ? 'brand.500' : 'transparent'}
                transition="background-color 150ms ease, color 150ms ease"
                fontSize="14px"
                fontWeight={isActive ? '500' : '400'}
                textAlign="left"
                border="none"
                cursor="pointer"
                _hover={{ bg: isActive ? 'bg.active' : 'bg.tertiary' }}
                whiteSpace="nowrap"
              >
                <NavIcon d={item.iconPath} active={isActive} />
                {isExpanded && item.label}
              </Box>
            );
          })}
        </Flex>

        {/* Bottom: theme toggle */}
        <Flex
          px={isExpanded ? '5' : '0'}
          justify={isExpanded ? 'flex-start' : 'center'}
          py="4"
          borderTop="1px solid"
          borderColor="border.secondary"
        >
          <ThemeCycleButton />
        </Flex>
      </Box>

      {/* Desktop main content — fixed margin for icon rail */}
      <Box
        as="main"
        display={{ base: 'block', md: 'block' }}
        flex="1"
        ml={{ base: '0', md: '64px' }}
        bg="bg.primary"
        overflow="auto"
      >
        <Box
          maxW="1200px"
          mx="auto"
          px={{ base: '4', md: '8' }}
          py={{ base: '4', md: '8' }}
          pb={{ base: '80px', md: '8' }}
        >
          <SystemStateBanner />
          {children}
        </Box>
      </Box>

      {/* Mobile bottom nav */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="bg.secondary"
        borderTop="1px solid"
        borderColor="border.secondary"
        zIndex="100"
        justify="space-around"
        py="2"
      >
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Box
              as="button"
              key={item.path}
              onClick={() => navigate(item.path)}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="2px"
              minW="64px"
              py="1"
              px="2"
              color={isActive ? 'text.brand' : 'text.tertiary'}
              bg="transparent"
              border="none"
              cursor="pointer"
              fontSize="11px"
              fontWeight={isActive ? '500' : '400'}
            >
              <NavIcon d={item.iconPath} active={isActive} />
              {item.label}
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}
